import { Grid, FormControlLabel, Checkbox, Button } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../hooks";
import QTDetailsImageWithLabelSection from "../../../../common/QTDetailsImageWithLabelSection";
import QTDetailsAudioWithLabelSection from "../../../../common/QTDetailsAudioWithLabelSection";
import QTDetailsTextFieldWithLabelSection from "../../../../common/QTDetailsTextFieldWithLabelSection";
import QTDetailsAutoCompleteWithLabelSection from "../../../../common/QTDetailsAutoCompleteWithLabelSection";
import { useContributorsContext } from "../../../../providers/OrganizationAdmin/ContributorsProvider";
import {
  capitalizeFirstLetterOfEveryWord,
  images,
  showNotification,
} from "../../../../constants";
import QTDetailsLocationWithLabelSection from "../../../../common/QTDetailsLocationWithLabelSection";
import QTDetailsLatLongSection from "../../../../common/QTDetailsLatLongSection";
import { dataContainerStyles } from "./styles";
import moment from "moment";
import { getCustomNarratorsByUser } from "../../hooks/CustomNarratorsHooks";
import QTAreasField from "./QTAreasField";
import { getCreatedByDisplay } from "../../../../utils/createdByUtils";


const getEmailDisplay = (email) => {
  const trimmed = (email || "").trim();
  return trimmed ? trimmed : "No email address";
};

// If we haven't resolved the email yet (async fetch in container), avoid
// showing the fallback text momentarily.
const getEmailSuffixWhenResolved = (email) => {
  if (email === undefined || email === null) return "";
  return `/${getEmailDisplay(email)}`;
};

const QTDetailsDataFieldsSection = ({
  quicktaleDetails,
  setQuicktaleDetails,
  handleImagePreviewModalOpen,
  handleAudioModalOpen,
  handleProcessedAudioModalOpen,
  editEnabled,
  canEditPhoto,
  canEditAudio,
  canEditTitle,
  canEditDescription,
  greetingText,
  uploadImage,
  setUploadImage,
  uploadAudioFile,
  setUploadAudioFile,
  uploadProcessedAudioFile,
  setUploadProcessedAudioFile,
  handleFileUpload,
  onProcessOriginalAudio,
  isCreateMode = false,
  isSaving,
  finishedCheckboxDisabled = false,
  isExternal = false,
  narratorError,
  onBatchNameClick,
  areasMode = "none",
  // Auto-approve toggle — only shown on the Batch QT Details page
  showAutoApprove = false,
  autoApprove = false,
  onAutoApproveChange,
  // The logged-in admin's organization. Enables a one-click "Set to Org"
  // button on the Created By field so the QT can be attributed to the org.
  currentOrganization = null,
  // Optional: persist the createdBy change immediately (used in view mode
  // where there is no Save button). Receives the new createdBy id.
  onCreatedBySet,
}) => {
  const { currentUser } = useAuth();

  const { GAUsers, handleGetAllGAUsers } = useContributorsContext();
  const [usersData, setUsersData] = useState([]);
  const [customNarrators, setCustomNarrators] = useState([]);
  const [mergedNarrators, setMergedNarrators] = useState([]);
  const [isDraftEnabled, setisDraftEnabled] = useState(true);

  // Auto-switch isDraft status based on required fields
  useEffect(() => {
    if (isSaving?.current) {
      // If we are saving, don't run this effect.
      // Reset the flag after a short delay to allow the save to complete.
      setTimeout(() => {
        isSaving.current = false;
      }, 100);
      return;
    }

    const hasAudio = !!(uploadAudioFile || quicktaleDetails.audioUrl);
    const hasImage = !!(uploadImage || quicktaleDetails.imageUrls?.[0]);
    const hasTitle = !!quicktaleDetails.title && quicktaleDetails.title.trim().length > 0;
    
    if (hasAudio && hasImage && hasTitle) {
      // Auto-switch to Finished when all three are present
      if (quicktaleDetails.isDraft !== false) {
        setQuicktaleDetails({
          ...quicktaleDetails,
          isDraft: false,
        });
        setisDraftEnabled(true);
      }
    } else {
      // Auto-switch to Draft when any element is missing
      //if (quicktaleDetails.isDraft !== true) {
        setQuicktaleDetails({
          ...quicktaleDetails,
          isDraft: true,
        });
        setisDraftEnabled(false);
      //}
    }
  }, [
    quicktaleDetails.audioName,
    quicktaleDetails.audioUrl,
    quicktaleDetails.photoUrl,
    quicktaleDetails.imageUrls,
    quicktaleDetails.title,
    uploadImage,
    uploadAudioFile,
  ]);

  useEffect(() => {
    if (GAUsers && GAUsers?.length > 0) {
    } else {
      handleGetAllGAUsers();
    }
  }, []);

  useEffect(() => {
    // Apply the same filtering and sorting logic that's in the provider
    let filteredUsers = GAUsers.filter(user => user !== null);

    // Filter out "Guest User" entries (first name + last name combined)
    filteredUsers = filteredUsers.filter(user => {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      return fullName !== "Guest User";
    });

    // Sort alphabetically by full name (case-insensitive)
    filteredUsers.sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.toLowerCase().trim();
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });

    setUsersData(filteredUsers);
  }, [GAUsers]);

  // Fetch custom narrators for the current user
  useEffect(() => {
    const fetchCustomNarrators = async () => {
      // Use userId from admin record if available, otherwise fall back to uid
      const userIdToUse = currentUser?.userId || currentUser?.uid;
      if (userIdToUse) {
        const narrators = await getCustomNarratorsByUser(userIdToUse);
        setCustomNarrators(narrators);
      }
    };

    fetchCustomNarrators();
  }, [currentUser]);

  // Refresh custom narrators when entering edit mode
  useEffect(() => {
    if (editEnabled) {
      const refreshCustomNarrators = async () => {
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        if (userIdToUse) {
          const narrators = await getCustomNarratorsByUser(userIdToUse);
          setCustomNarrators(narrators);
        }
      };
      refreshCustomNarrators();
    }
  }, [editEnabled, currentUser]);

  // Merge users and custom narrators, then sort alphabetically
  useEffect(() => {
    // Transform custom narrators to match the user data structure
    const transformedCustomNarrators = customNarrators.map(narrator => ({
      id: null, // Custom narrators don't have user IDs
      fullName: narrator.name,
      // No firstName, lastName, or userName fields - this distinguishes them from users
    }));

    // Merge users and custom narrators
    const merged = [...usersData, ...transformedCustomNarrators];

    // Sort alphabetically by display name (case-insensitive)
    merged.sort((a, b) => {
      const nameA = a.firstName && a.lastName 
        ? `${a.firstName} ${a.lastName}`.toLowerCase().trim()
        : (a.fullName || '').toLowerCase().trim();
      const nameB = b.firstName && b.lastName 
        ? `${b.firstName} ${b.lastName}`.toLowerCase().trim()
        : (b.fullName || '').toLowerCase().trim();
      return nameA.localeCompare(nameB);
    });

    setMergedNarrators(merged);
  }, [usersData, customNarrators]);

  return (
    <Grid container style={dataContainerStyles}>
      {/*left side column*/}
      <Grid item container direction={"column"} xs={isExternal ? 12 : true}>
        {/* Greeting (external only) */}
        {isExternal && greetingText ? (
          <Grid item style={{ marginBottom: 16, paddingLeft: "2%" }}>
            <span
              style={{
                fontSize: 16,
                color: "#020303",
                fontFamily: "Avenir Book, sans-serif",
                whiteSpace: "pre-line",
              }}
            >
              {greetingText}
            </span>
          </Grid>
        ) : null}

        {/*photo section*/}
        <QTDetailsImageWithLabelSection
          label={"QT Photo"}
          image={
            quicktaleDetails?.photoUrl
              ? quicktaleDetails?.photoUrl
              : quicktaleDetails?.imageUrls?.[0]
              ? quicktaleDetails?.imageUrls[0]
              : isCreateMode
              ? null
              : images.QuicktaleLogo
          }
          // image={images.tableDummyImage}
          fileName={
            quicktaleDetails?.imageFileNames?.[0] || 
            quicktaleDetails?.originalPhotoName
          }
          handleImagePreviewModalOpen={() => {
            handleImagePreviewModalOpen();
          }}
          editEnabled={isExternal ? !!(editEnabled && canEditPhoto !== false) : editEnabled}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadImage={uploadImage}
          setUploadImage={setUploadImage}
          isCreateMode={isCreateMode}
          autoOpenCropOnUpload={true}
        />
        {/*original audio section*/}
        <QTDetailsAudioWithLabelSection
          label={`Original Audio File${!quicktaleDetails.processedAudioUrl ? '*' : ''}`}
          handleAudioModalOpen={handleAudioModalOpen}
          editEnabled={isExternal ? !!(editEnabled && canEditAudio !== false) : editEnabled}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadAudioFile={uploadAudioFile}
          setUploadAudioFile={setUploadAudioFile}
          handleFileUpload={(file, _type, source) => handleFileUpload(file, "original", source)}
          showProcessButton={!isExternal}
          onProcessAudio={onProcessOriginalAudio}
        />
        {/*processed audio section*/}
        {!isExternal && <QTDetailsAudioWithLabelSection
          label={`Processed Audio File${quicktaleDetails.processedAudioUrl ? '*' : ''}`}
          handleAudioModalOpen={handleProcessedAudioModalOpen}
          editEnabled={editEnabled}
          details={quicktaleDetails}
          setDetails={setQuicktaleDetails}
          uploadAudioFile={uploadProcessedAudioFile}
          setUploadAudioFile={setUploadProcessedAudioFile}
          audioNameField="processedAudioName"
          audioUrlField="processedAudioUrl"
          hideUploadButton={true}
          handleFileUpload={(file) => handleFileUpload(file, "processed")}
        />}
        {!isExternal && (
          <Grid item style={{ marginTop: '-24px', marginBottom: 12, paddingLeft: "2%" }}>
            <span style={{ fontSize: 14, color: "#666", fontStyle: "italic" }}>
              *This is the audio that will be played in the app.
            </span>
          </Grid>
        )}
        {/*QT ID section - hidden in create mode*/}
        {!isExternal && !isCreateMode && (
          <QTDetailsTextFieldWithLabelSection
            label={"QT ID"}
            value={quicktaleDetails.id}
            disabled={true}
            // editEnabled={editEnabled}
            onChange={(e) =>
              setQuicktaleDetails({
                ...quicktaleDetails,
                id: e.target.value,
              })
            }
          />
        )}
        {/*Creation Date Section - hidden in create mode*/}
        {!isExternal && !isCreateMode && (
          <QTDetailsTextFieldWithLabelSection
            label={"Creation Date"}
            value={moment.unix(quicktaleDetails?.createdAt.seconds).format("MM/DD/YYYY HH:mm:ss")}
            disabled={true}
          />
        )}
        {/*QT Title section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Title"}
          value={quicktaleDetails.title}
          disabled={true}
          capitalizeWords
          editEnabled={isExternal ? !!(editEnabled && canEditTitle !== false) : editEnabled}
          placeholder={isCreateMode ? `Untitled ${moment().format("M-D-YYYY")}` : ""}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              title: e.target.value,
              // title: e.target.value,
            })
          }
        />
        {/*Description section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Description"}
          value={quicktaleDetails.description}
          disabled={true}
          multiline={true}
          rows={5}
          editEnabled={isExternal ? !!(editEnabled && canEditDescription !== false) : editEnabled}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              description: e.target.value,
            })
          }
        />

        {/* Areas */}
        {!isExternal && (
          <QTAreasField
            areasMode={areasMode}
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            editEnabled={editEnabled}
          />
        )}

        {/* Batch Name (for promoted batch quicktales) */}
        {!isExternal && quicktaleDetails?.batchId && quicktaleDetails?.batchName && (
          <Grid
            container
            alignItems={"center"}
            style={{ marginTop: 8, marginBottom: 24 }}
          >
            {/* Label styled like other field labels */}
            <span
              style={{
                width: "25%",
                color: "#020303",
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "Avenir Book, sans-serif",
              }}
            >
              Batch Name
            </span>
            {/* Value aligned with other field content */}
            <span
              onClick={onBatchNameClick}
              style={{
                width: "70%",
                cursor: onBatchNameClick ? "pointer" : "default",
                textDecoration: onBatchNameClick ? "underline" : "none",
                fontSize: 14,
                fontWeight: 500,
                color: "#00A7CD", // Sidebar background blue
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}
            >
              {quicktaleDetails.batchName}
            </span>
          </Grid>
        )}
      </Grid>
      {/*right side column*/}
      {!isExternal && <Grid item container direction={"column"} xs>
        {/*Created By section*/}
        <QTDetailsAutoCompleteWithLabelSection
          label={"Created By"}
          value={(() => {
            // No resolved data yet, but the raw id matches the admin's own org:
            // show the org name instead of the bare id.
            if (
              quicktaleDetails?.createdByData == null &&
              currentOrganization?.id &&
              quicktaleDetails?.createdBy === currentOrganization.id
            ) {
              return currentOrganization.name || quicktaleDetails?.createdBy || "";
            }
            // Shared resolver: handles user vs organization display.
            return getCreatedByDisplay(
              quicktaleDetails?.createdByData,
              quicktaleDetails?.createdBy || ""
            );
          })()}

          onChange={(docData) => {
            if (docData.id) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                createdByData: docData,
                createdBy: docData.id,
              });
            } else {
              setQuicktaleDetails({
                ...quicktaleDetails,
                createdByData: null,
                // createdBy: docData.fullName,
                createdBy: quicktaleDetails.createdBy,
              });
            }
          }}
          disabled={true}
          optionsData={usersData}
          editEnabled={editEnabled}
        />
        {/* "Set to Org" — one-click attribution of Created By to the admin's own
            organization. Available in both view and edit mode. Stores
            organizations.id in createdBy (no type flag). */}
        {currentOrganization?.id && (
          <Grid container alignItems={"flex-start"} style={{ marginTop: -20, marginBottom: 16 }}>
            {/* Sits directly under the "Created By" label */}
            <div style={{ width: "25%", paddingLeft: "2%" }}>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  setQuicktaleDetails({
                    ...quicktaleDetails,
                    createdBy: currentOrganization.id,
                    createdByData: {
                      ...currentOrganization,
                      id: currentOrganization.id,
                      _sourceType: "organization",
                    },
                  });
                  // In view mode there is no Save button, so persist right away.
                  // In edit mode the change is saved with the rest of the form.
                  if (!editEnabled && typeof onCreatedBySet === "function") {
                    onCreatedBySet(currentOrganization.id);
                  } else {
                    showNotification("Created By updated successfully!");
                  }
                }}
                sx={{
                  textTransform: "none",
                  color: "#00A7CD",
                  fontFamily: "Avenir Book, sans-serif",
                  fontSize: 11,
                  padding: "0 4px",
                  minWidth: 0,
                  marginTop: '-40px',
                  marginLeft: '-20px',
                }}
              >
                Set to {currentOrganization.name || "Org"}
              </Button>
            </div>
            <span style={{ width: "70%" }} />
          </Grid>
        )}
        {/* <QTDetailsTextFieldWithLabelSection
          label={"Created By"}
          value={
            quicktaleDetails.createdByData &&
            quicktaleDetails.createdByData?.userName
          }
          disabled={true}
        /> */}
        {/*Told By section*/}
        <QTDetailsAutoCompleteWithLabelSection
          label={"Told By*"}
          value={
            quicktaleDetails?.narratorData != null
              ? (() => {
                  const displayName = `${quicktaleDetails?.narratorData?.firstName} ${quicktaleDetails?.narratorData?.lastName}`;
                  const emailSuffix = getEmailSuffixWhenResolved(quicktaleDetails?.narratorData?.email);
                  return quicktaleDetails?.narratorData?.userName
                    ? `${displayName}/@${quicktaleDetails?.narratorData?.userName}${emailSuffix}`
                    : `${displayName}${emailSuffix}`;
                })()
              : (() => {
                  const name = quicktaleDetails?.narrator || "";
                  const emailSuffix = getEmailSuffixWhenResolved(
                    quicktaleDetails?.customNarratorDoc?.email
                  );
                  return name ? `${name}${emailSuffix}` : "";
                })()
          }
          onChange={(docData) => {
            if (docData.id) {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: docData,
                narrator: docData.id,
              });
            } else {
              setQuicktaleDetails({
                ...quicktaleDetails,
                narratorData: null,
                narrator: docData.fullName,
              });
            }
          }}
          disabled={true}
          optionsData={mergedNarrators}
          editEnabled={editEnabled}
          error={narratorError}
        />
        {/*who Can Listen section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Who Can Listen"}
          value={quicktaleDetails.status}
          disabled={true}
          editEnabled={editEnabled}
          select={true}
          dropdownValues={["public", "private", "unlisted"]}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              status: e.target.value,
            })
          }
        />
        {/*length section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Length"}
          value={
            quicktaleDetails?.audioLength
              ? (() => {
                  const minutes = Math.floor(quicktaleDetails.audioLength / 60);
                  const seconds = quicktaleDetails.audioLength % 60;
                  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                })()
              : ""
          }
          disabled={true}
        />
        {/*qt Status section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Org Status"}
          value={quicktaleDetails?.approvalStatus?.status}
          disabled={!editEnabled}
          editEnabled={editEnabled}
          select={true}
          dropdownValues={["approved", "pending", "denied", "removed"]}
          onChange={(e) => {
            let temp = {
              status: e.target.value,
              updatedAt: new Date(),
              updatedBy: currentUser?.userName,
            };
            setQuicktaleDetails({
              ...quicktaleDetails,
              approvalStatus: temp,
            });
          }}
        />
        {/* Auto-approve checkbox — batch QT details page only */}
        {showAutoApprove && (
          <Grid container alignItems="center" style={{ marginBottom: 16 }}>
            <span
              style={{
                width: "25%",
                color: "#020303",
                fontSize: 14,
                fontWeight: 700,
                fontFamily: "Avenir Book, sans-serif",
              }}
            >
              Auto Approve
            </span>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!autoApprove}
                  onChange={onAutoApproveChange}
                  disabled={!editEnabled}
                  sx={{ color: "#00A7CD", "&.Mui-checked": { color: "#00A7CD" } }}
                />
              }
              label={
                <span style={{ fontSize: 14, fontFamily: "Avenir Book, sans-serif" }}>
                  {autoApprove ? "On" : "Off"}
                </span>
              }
              style={{ margin: 0 }}
            />
          </Grid>
        )}
        {/*tags section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"Tags"}
          value={quicktaleDetails.tags}
          disabled={true}
          editEnabled={editEnabled}
          onChange={(e) =>
            setQuicktaleDetails({
              ...quicktaleDetails,
              tags: e.target.value,
            })
          }
        />

        {/*location section*/}
        <QTDetailsLocationWithLabelSection
          label={"Location"}
          locationDisplayValue={quicktaleDetails?.locationDisplay}
          value={quicktaleDetails?.location || ""}
          quicktaleDetails={quicktaleDetails}
          disabled={true}
          editEnabled={editEnabled}
          onChange={(docData) => {
            console.log("doc data::", docData);
            setQuicktaleDetails({
              ...quicktaleDetails,
              coordinates: docData.coordinates,
              location: docData.location,
              locationDisplay: docData.locationDisplay,
              locationCountry: docData.locationCountry,
              g: docData.g,
            });
            // setQuicktaleDetails({ ...quicktaleDetails, ...docData });
          }}
        />

        {/*Geo Hash section - hidden in create mode*/}
        {!isCreateMode && (
          <QTDetailsTextFieldWithLabelSection
            label={"Geohash"}
            value={quicktaleDetails?.g?.geohash}
            disabled={true}
          />
        )}
        {/*Lat/long section*/}
        {/* <QTDetailsLatLongSection
          label={"Lat/Long"}
          quicktaleDetails={quicktaleDetails}
          // latValue={quicktaleDetails?.coordinates?._lat}
          // longValue={quicktaleDetails?.coordinates?._long}
          onUpdate={(data) => {
            setQuicktaleDetails({
              ...quicktaleDetails,
              coordinates: data.coordinates,
              location: data.location,
              g: data.g,
            });
          }}
          disabled={true}
          editEnabled={editEnabled}
        /> */}

        <QTDetailsLatLongSection
          label={"Lat/Long"}
          // quicktaleDetails={quicktaleDetails}
          value={
            quicktaleDetails?.coordinates?._lat +
            "," +
            quicktaleDetails?.coordinates?._long
          }
          latValue={quicktaleDetails?.coordinates?._lat}
          longValue={quicktaleDetails?.coordinates?._long}
          onChange={(data) => {
            setQuicktaleDetails({
              ...quicktaleDetails,
              coordinates: data.coordinates,
              location: data.location,
              locationDisplay: data.locationDisplay,
              locationCountry: data.locationCountry,
              g: data.g,
            });
          }}
          disabled={true}
          editEnabled={editEnabled}
        />
        {/*QT Status section*/}
        <QTDetailsTextFieldWithLabelSection
          label={"QT Status"}
          value={quicktaleDetails?.isDraft === true ? "Draft" : "Finished"}
          disabled={editEnabled && isDraftEnabled ? false : true}
          editEnabled={editEnabled === true ? isDraftEnabled : false}
          select={true}
          dropdownValues={["Draft", "Finished"]}
          onChange={(e) => {
            setQuicktaleDetails({
              ...quicktaleDetails,
              isDraft: e.target.value === "Draft" ? true : false,
            });
          }}
        />
      </Grid>}
    </Grid>
  );
};

export default QTDetailsDataFieldsSection;
